import Image from 'next/image'
import Link from 'next/link'
import type {ReactNode} from 'react'
import {ChevronLink} from '@/components/shared/ChevronLink'
import {buttonVariants} from '@/components/ui/button-variants'
import {cn} from '@/lib/utils'
import {t} from '@/lib/i18n'
import {urlForImage} from '@/sanity/image'
import type {ArticleDocument} from '@/types/sanity'

type ArticlesGridProps = {
  articles: ArticleDocument[]
  fallbackImageUrl?: string
  locale?: string
}

export type ArticleCardProps = {
  href: string
  imageUrl?: string
  imageAlt?: string
  eyebrow?: string
  title: string
  subtitle?: string
  excerpt?: string
  ctaLabel?: string
  ctaVariant?: 'link' | 'button'
  /** Content rendered above the title (badges, tags) */
  meta?: ReactNode
  layout?: 'vertical' | 'horizontal'
  className?: string
  equalizeContentHeight?: boolean
  /** Active site locale (e.g. "nb-NO"). Used for default CTA label. */
  locale?: string
}

const articleIdentity = (article: ArticleDocument) => {
  const normalizedId = article._id.replace(/^drafts\./, '')
  const slug = article.slug?.current || ''
  return slug ? `${normalizedId}::${slug}` : normalizedId
}

export const uniqueArticles = (articles: ArticleDocument[]) => {
  const seen = new Set<string>()

  return articles.filter((article) => {
    const identity = articleIdentity(article)
    if (seen.has(identity)) {
      return false
    }
    seen.add(identity)
    return true
  })
}

const hrefFromSlug = (slug?: string) => {
  if (!slug) return '#'
  return slug.startsWith('/') ? slug : `/${slug}`
}

const articleImageUrl = (article: ArticleDocument) => {
  if (!article.heroImage?.image) return undefined
  return urlForImage(article.heroImage.image).width(960).height(640).fit('crop').url()
}

const articleCategoryLabel = (article: ArticleDocument) => {
  const category = article.categories?.[0]
  return category?.siteVariant?.title || category?.title || null
}

export function ArticleCard({
  href,
  imageUrl,
  imageAlt = '',
  eyebrow,
  title,
  subtitle,
  excerpt,
  ctaLabel,
  ctaVariant = 'link',
  meta,
  layout = 'vertical',
  className,
  equalizeContentHeight = false,
  locale,
}: ArticleCardProps) {
  const resolvedCtaLabel = ctaLabel || t(locale, 'common.readMore', 'Read more')
  const isHorizontal = layout === 'horizontal'

  return (
    <article className={cn('group h-full u-card', className)}>
      <Link
        href={href}
        className={cn('block h-full', isHorizontal && 'lg:grid lg:grid-cols-[1.05fr_0.95fr]')}
      >
        <div
          className={cn(
            'relative overflow-hidden bg-muted',
            isHorizontal ? 'min-h-[280px] lg:min-h-full' : 'aspect-[16/10]',
          )}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={imageAlt}
              fill
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
              className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          ) : (
            <div className="h-full w-full bg-muted" />
          )}
        </div>
        <div
          className={cn(
            'flex flex-col',
            isHorizontal
              ? 'px-site-xl py-site-xl lg:px-site-section-sm lg:py-site-section-sm'
              : 'min-h-[250px] px-site-lg py-site-md',
            equalizeContentHeight && !isHorizontal && 'min-h-[280px]',
          )}
        >
          {meta}
          {eyebrow && !meta ? (
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {eyebrow}
            </p>
          ) : null}
          <h2
            className={cn(
              'u-head-sm text-foreground',
              meta && 'mt-site-sm',
              eyebrow && !meta && 'mt-1',
            )}
          >
            {title}
          </h2>
          {subtitle ? <p className="mt-site-sm u-head-xs text-foreground">{subtitle}</p> : null}
          {excerpt ? (
            <p
              className={cn(
                'mt-site-sm u-body-md text-muted-foreground',
                !isHorizontal && 'max-w-[28ch]',
                equalizeContentHeight && !isHorizontal && 'line-clamp-3',
              )}
            >
              {excerpt}
            </p>
          ) : null}
          <div className="mt-auto pt-site-lg">
            {ctaVariant === 'button' ? (
              <span className={buttonVariants()}>{resolvedCtaLabel}</span>
            ) : (
              <ChevronLink
                className={`${buttonVariants({variant: 'link'})} group-hover:decoration-2`}
                groupHover
              >
                {resolvedCtaLabel}
              </ChevronLink>
            )}
          </div>
        </div>
      </Link>
    </article>
  )
}

/** Convenience wrapper — maps an ArticleDocument to ArticleCard props */
export function ArticleDocumentCard({
  article,
  fallbackImageUrl,
  cardClassName,
  equalizeContentHeight = false,
  locale,
}: {
  article: ArticleDocument
  fallbackImageUrl?: string
  cardClassName?: string
  equalizeContentHeight?: boolean
  locale?: string
}) {
  return (
    <ArticleCard
      href={hrefFromSlug(article.slug?.current)}
      imageUrl={articleImageUrl(article) || fallbackImageUrl}
      imageAlt={article.heroImage?.alt || article.title || ''}
      eyebrow={articleCategoryLabel(article) || t(locale, 'articles.defaultCategory', 'Article')}
      title={article.title || t(locale, 'articles.untitled', 'Untitled')}
      excerpt={article.excerpt}
      className={cardClassName}
      equalizeContentHeight={equalizeContentHeight}
      locale={locale}
    />
  )
}

export function ArticlesGrid({articles, fallbackImageUrl, locale}: ArticlesGridProps) {
  const dedupedArticles = uniqueArticles(articles)

  if (!dedupedArticles.length) {
    return (
      <p className="text-muted-foreground">
        {t(locale, 'articles.noResults', 'No articles found.')}
      </p>
    )
  }
  return (
    <div className="grid grid-cols-1 gap-card-gap md:grid-cols-2 xl:grid-cols-3">
      {dedupedArticles.map((article) => (
        <ArticleDocumentCard
          key={articleIdentity(article)}
          article={article}
          fallbackImageUrl={fallbackImageUrl}
          locale={locale}
        />
      ))}
    </div>
  )
}
